/**
 * CONSULT REQUEST — one button on the clinic's tap card, one screen, one event.
 *
 * Name plus ONE of phone or email. Nothing clinical is asked, nothing is
 * booked, no photos. The clinic reads it in its Command Center and the first
 * reply claims it.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarCheck, Loader2, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = (typeof window !== 'undefined' ? window.location.origin : '');

export default function MedicalConsultCard({ cardId }) {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const ready = name.trim().length > 1 && (phone.trim().length > 5 || email.includes('@'));

    const submit = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await fetch(`${API}/api/public/medical/consult/${cardId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: name.trim(),
                    phone: phone.trim() || null,
                    email: email.trim() || null,
                    language: (i18n.language || 'en').slice(0, 2),
                }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setDone(true);
        } catch (e) {
            setError(t('reviewShield.consult.error', 'That did not go through. Please try again.'));
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4"
                data-testid="consult-done">
                <div className="flex items-center gap-2.5">
                    <Check className="w-4.5 h-4.5 text-emerald-400 shrink-0" />
                    <p className="text-sm text-white" data-testid="consult-done-text">
                        {t('reviewShield.consult.done', 'Thank you. The clinic will contact you.')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-4" data-testid="consult-card">
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    className="w-full rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-3.5 flex items-center justify-center gap-2.5 text-sm text-white transition-colors"
                    data-testid="consult-open-btn"
                >
                    <CalendarCheck className="w-4.5 h-4.5 text-emerald-400" />
                    {t('reviewShield.consult.cta', 'Request a consult')}
                </button>
            )}

            {open && (
                <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-2.5"
                    data-testid="consult-form">
                    <p className="text-sm font-medium text-white" data-testid="consult-form-title">
                        {t('reviewShield.consult.title', 'Request a consult')}
                    </p>
                    <p className="text-xs text-slate-300" data-testid="consult-form-help">
                        {t('reviewShield.consult.help', 'Leave your name and one way to reach you.')}
                    </p>
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('reviewShield.consult.namePlaceholder', 'Your name')}
                        className="w-full rounded-lg bg-slate-900/60 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500"
                        data-testid="consult-name"
                    />
                    <input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        inputMode="tel"
                        placeholder={t('reviewShield.consult.phonePlaceholder', 'Phone (optional)')}
                        className="w-full rounded-lg bg-slate-900/60 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500"
                        data-testid="consult-phone"
                    />
                    <input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                        placeholder={t('reviewShield.consult.emailPlaceholder', 'Email (optional)')}
                        className="w-full rounded-lg bg-slate-900/60 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500"
                        data-testid="consult-email"
                    />
                    <p className="text-xs text-amber-300/90" data-testid="consult-no-medical-note">
                        {t('reviewShield.consult.noMedical', 'Please do not include medical details.')}
                    </p>
                    {error && <p className="text-xs text-rose-300" data-testid="consult-error">{error}</p>}
                    <Button
                        onClick={submit}
                        disabled={busy || !ready}
                        className="h-10 w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-40"
                        data-testid="consult-submit-btn"
                    >
                        {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        {t('reviewShield.consult.submit', 'Send the request')}
                    </Button>
                </div>
            )}
        </div>
    );
}
